/**
 * main.ts
 *
 * Bootstraps Vuetify and other plugins then mounts the App`
 */

// Plugins
import { registerPlugins } from '@/plugins'

// Components
import App from './App.vue'

// Composables
import { createApp } from 'vue'
import { useConfigStore } from '@/stores/config'
import { useAuthStore } from '@/stores/auth'
import { setupAuthInterceptors } from '@/api/client'
import router from '@/router'
import apiClient from '@/api/client'

// Styles
import 'unfonts.css'
import '@/assets/styles/variables.css'

const app = createApp(App)

app.config.errorHandler = (err, instance, info) => {
  console.error('[Vue error]', err)
  console.error('[Component]', instance?.$options?.__name ?? 'unknown')
  console.error('[Lifecycle hook]', info)
}

// Register plugins first (installs Pinia, router, Vuetify)
registerPlugins(app)

// Wire up 401 auto-refresh after Pinia is ready
const auth = useAuthStore()
setupAuthInterceptors(async (failedConfig) => {
  try {
    const newToken = await auth.refreshAccessToken()
    if (failedConfig.headers) {
      failedConfig.headers['Authorization'] = `Bearer ${newToken}`
    }
    return apiClient(failedConfig)
  } catch {
    auth.clearTokens()
    router.push({ name: 'login' })
    return Promise.reject({ code: 'UNAUTHORIZED', message: 'Session expired', details: {} })
  }
})

// Load app mode from health endpoint, then attempt silent token restoration
const config = useConfigStore()
config
  .loadAppMode()
  .then(async () => {
    if (config.appMode === 'prod' && localStorage.getItem('accessToken')) {
      try {
        await auth.fetchMe()
      } catch {
        try {
          await auth.refreshAccessToken()
          await auth.fetchMe()
        } catch {
          auth.clearTokens()
        }
      }
    }
  })
  .finally(() => {
    app.mount('#app')
  })
